/**
 * Central API client (Phase 8).
 *
 * Two responsibilities:
 *
 *  1. `apiFetch` / `apiJson` — a thin wrapper new P8 screens use so they never
 *     hand-roll the Authorization / X-Tenant-Id / Content-Type headers.
 *
 *  2. `installTenantHeaderInterceptor` — a one-time global `window.fetch` patch
 *     that stamps the active tenant onto EVERY request to our backend that
 *     doesn't already carry an `X-Tenant-Id`. This is how a ROOT/multi-tenant
 *     user's active tenant "flows to the backend via header" (P8 DoD) for the
 *     existing screens too, without editing ~15 scattered fetch utilities.
 *
 * The server re-resolves and re-authorizes the tenant on every request
 * (TenantResolutionFilter, §4). This header is a routing hint, never a grant.
 */

import { wupoServerUri } from '../Api';
import { getSessionToken, getActiveTenantId } from './session';

const TENANT_HEADER = 'X-Tenant-Id';

const backendOrigin = () => {
  try { return new URL(wupoServerUri.devnet).origin; } catch (e) { return null; }
};

// ── request helper for new screens ────────────────────────────────────────

/**
 * Fetch a backend path with auth + tenant headers attached.
 * @param {string} path  e.g. "/api/admin/rbac/roles"
 * @param {object} opts  { method, body (object → JSON), token, headers }
 */
export const apiFetch = (path, opts = {}) => {
  const { method = 'GET', body, token, headers = {} } = opts;
  const tenantId = getActiveTenantId();
  const authToken = token || getSessionToken();

  const finalHeaders = {
    'Content-Type': 'application/json; charset=UTF-8',
    ...headers,
  };
  if (authToken) finalHeaders['Authorization'] = `Bearer ${authToken}`;
  if (tenantId && !finalHeaders[TENANT_HEADER]) finalHeaders[TENANT_HEADER] = tenantId;

  return fetch(`${wupoServerUri.devnet}${path}`, {
    method,
    credentials: 'include',
    headers: finalHeaders,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
};

/**
 * apiFetch + normalized result: { ok, status, data, error }.
 * `data` is the parsed JSON body (or null); `error` is a best-effort message.
 */
export const apiJson = async (path, opts = {}) => {
  try {
    const res = await apiFetch(path, opts);
    let data = null;
    const text = await res.text();
    if (text) {
      try { data = JSON.parse(text); } catch (e) { data = text; }
    }
    if (res.ok) return { ok: true, status: res.status, data, error: null };
    const error = (data && (data.error || data.message)) || `Request failed (${res.status})`;
    return { ok: false, status: res.status, data, error };
  } catch (e) {
    return { ok: false, status: 0, data: null, error: e.message || 'Network error' };
  }
};

// ── global interceptor ────────────────────────────────────────────────────

let installed = false;

/**
 * Patch window.fetch once so backend calls carry the active tenant header.
 * Idempotent; safe to call at app bootstrap.
 */
export const installTenantHeaderInterceptor = () => {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const origin = backendOrigin();
  const nativeFetch = window.fetch.bind(window);

  window.fetch = (input, init) => {
    try {
      const tenantId = getActiveTenantId();
      if (tenantId && origin) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const targetsBackend = url.startsWith(origin) || url.startsWith(wupoServerUri.devnet);
        if (targetsBackend) {
          // Normalize whatever header shape the caller passed into a Headers object.
          const headers = new Headers((init && init.headers) || (typeof input !== 'string' && input && input.headers) || {});
          if (!headers.has(TENANT_HEADER)) {
            headers.set(TENANT_HEADER, tenantId);
            return nativeFetch(input, { ...(init || {}), headers });
          }
        }
      }
    } catch (e) {
      // Never let header injection break a request — fall through to native.
    }
    return nativeFetch(input, init);
  };
};
